import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker, Field, inputCls } from "@/components/form";
import { api, formatApiError } from "@/lib/api";
import { AREA_OPTIONS } from "@/data/site";

export default function Terminbuchung() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_area: "versicherung", preferred_date: "", preferred_time: "", message: "", gdpr_consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/appointments", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", business_area: "versicherung", preferred_date: "", preferred_time: "", message: "", gdpr_consent: false });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="termin-page" className="bg-navy-900 min-h-screen">
      <Seo title="Termin buchen" description="Vereinbaren Sie online ein kostenloses Beratungsgespräch mit VM-FFM.DE." path="/termin-buchen" />
      <section className="pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-left">
            <Kicker>Terminbuchung</Kicker>
            <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tighter text-white leading-none flex items-center gap-4"><CalendarClock className="text-gold-500 hidden sm:block" size={44} strokeWidth={1.4} /> Beratung vereinbaren</h1>
            <p className="mt-6 text-slate-300 leading-relaxed">Wählen Sie Ihren Wunschtermin – wir bestätigen ihn per E-Mail inklusive Kalendereinladung.</p>
          </motion.div>
          <motion.form data-testid="termin-form" onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 bg-navy-800 border border-white/5 rounded-md p-8 lg:p-10 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name *"><input data-testid="termin-name" required className={inputCls} value={form.name} onChange={set("name")} /></Field>
              <Field label="Telefon"><input data-testid="termin-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
            </div>
            <Field label="E-Mail *"><input data-testid="termin-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
            <Field label="Themenbereich"><select data-testid="termin-area" className={inputCls} value={form.business_area} onChange={set("business_area")}>{AREA_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-navy-800">{o.label}</option>)}</select></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Wunschdatum *"><input data-testid="termin-date" required type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} /></Field>
              <Field label="Wunschzeit *"><input data-testid="termin-time" required type="time" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")} /></Field>
            </div>
            <Field label="Anmerkung"><textarea data-testid="termin-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} /></Field>
            <label className="flex items-start gap-3 text-xs text-slate-400 cursor-pointer">
              <input data-testid="termin-gdpr" type="checkbox" checked={form.gdpr_consent} onChange={set("gdpr_consent")} className="mt-0.5 accent-gold-500 w-4 h-4" />
              <span>Ich stimme der Verarbeitung meiner Daten gemäß Datenschutzerklärung zu.</span>
            </label>
            <button data-testid="termin-submit" disabled={loading} className="w-full bg-gold-500 text-navy-900 font-bold rounded-sm py-4 hover:bg-gold-300 transition-colors disabled:opacity-60">{loading ? "Wird gesendet…" : "Termin anfragen"}</button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
